import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { FUNCTIONAL } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstateWaterfall_TransferLogItemFragment } from '../../../graphql/EstateWaterfall.generated';
import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';
import { getTrustTermDisplay } from './utils';

interface CltRemainderCardParams {
  sourceEntityName: string;
  targetEntityName: string;
  remainder?: EstateWaterfall_TransferLogItemFragment;
  cltFunding?: EstateWaterfall_TransferLogItemFragment;
  associatedIncomePayments?: EstateWaterfall_TransferLogItemFragment[];
}

export function createCltRemainderCard({
  sourceEntityName,
  targetEntityName,
  remainder,
  cltFunding,
  associatedIncomePayments = [],
}: CltRemainderCardParams): CardConfig {
  const cltName = sourceEntityName;
  const beneficiary = targetEntityName;

  const isHypothetical = remainder?.isHypothetical;

  // Get remainder value
  const remainderValue = remainder?.finalTransferAmount || new Decimal(0);

  // Calculate total income payments
  const totalIncomePayments = associatedIncomePayments.reduce(
    (sum, payment) => sum.plus(payment.finalTransferAmount || 0),
    new Decimal(0)
  );

  const fundingAmount = (() => {
    if (cltFunding) {
      return cltFunding.finalTransferAmount;
    }

    return null;
  })();

  const term = (() => {
    if (remainder?.associatedObject?.__typename === 'HypotheticalCLT') {
      const termYears =
        remainder.associatedObject.simulatedClt.cltProposal?.termYears;
      return termYears ? `${termYears} years` : null;
    }

    if (remainder?.associatedObject?.__typename === 'Entity') {
      if (remainder.associatedObject.subtype?.__typename === 'CLTTrust') {
        const { termDurationLifetime, termDurationYears } =
          remainder.associatedObject.subtype;
        return getTrustTermDisplay({ termDurationLifetime, termDurationYears });
      }
      return null;
    }

    return null;
  })();

  const termEndYear = (() => {
    if (remainder?.associatedObject?.__typename === 'HypotheticalCLT') {
      const termYears =
        remainder.associatedObject.simulatedClt.cltProposal?.termYears;
      return termYears ? new Date().getFullYear() + termYears : null;
    }

    if (remainder?.associatedObject?.__typename === 'Entity') {
      if (remainder.associatedObject.subtype?.__typename === 'CLTTrust') {
        const { termDurationLifetime } = remainder.associatedObject.subtype;
        if (termDurationLifetime) {
          return null;
        }
        return remainder.associatedObject.subtype.termEndDate?.getFullYear();
      }
      return null;
    }

    return null;
  })();

  return {
    element: (
      <EdgeLabelCard
        key={`clt-remainder-${sourceEntityName}-${targetEntityName}`}
        title={isHypothetical ? 'Hypothetical CLT remainder' : 'CLT remainder'}
        titleSx={
          isHypothetical ? { color: FUNCTIONAL.WARNING[600] } : undefined
        }
        description={`Remaining balance from ${cltName} distributed to ${beneficiary} at term end.`}
        rows={compact([
          fundingAmount
            ? {
                label: 'Funding amount',
                value: formatCurrencyNoDecimals(fundingAmount),
              }
            : null,
          totalIncomePayments.gt(0)
            ? {
                label: 'Total income payments',
                value: formatCurrencyNoDecimals(totalIncomePayments),
              }
            : null,
          term
            ? {
                label: 'CLT term',
                value: term,
              }
            : null,
          termEndYear
            ? {
                label: 'Term end',
                value: termEndYear.toString(),
              }
            : null,
          remainderValue.gt(0)
            ? {
                label: 'Remainder',
                value: formatCurrencyNoDecimals(remainderValue),
              }
            : null,
        ])}
      />
    ),
    label: 'CLT remainder',
    value: formatCurrencyNoDecimals(remainderValue),
    variant: isHypothetical ? 'secondary' : 'primary',
  };
}
